import React, { useEffect, useRef } from "react";
import Chart from "chart.js";

// 1. Définition des types pour les props
interface CardLineChartProps {
  title: string;
  subtitle?: string;
  data: number[]; // Tableau de montants
  labels: string[]; // Tableau de noms de mois
  loading?: boolean;
}

export default function CardLineChart({
  title,
  subtitle,
  data,
  labels,
  loading = false,
}: CardLineChartProps) {
  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const chartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    // On n'initialise le graphique que si on a des données et qu'on ne charge pas
    if (chartRef.current && data && data.length > 0 && !loading) {
      const ctx = chartRef.current.getContext("2d");

      if (ctx) {
        if (chartInstance.current) {
          chartInstance.current.destroy();
        }

        chartInstance.current = new Chart(ctx, {
          type: "line",
          data: {
            labels: labels,
            datasets: [
              {
                label: "Total Cotisations",
                backgroundColor: "rgba(212, 175, 55, 0.1)",
                borderColor: "#D4AF37",
                data: data,
                fill: true,
                lineTension: 0.4,
              },
            ],
          },
          options: {
            maintainAspectRatio: false,
            responsive: true,
            title: { display: false },
            legend: { display: false },
            tooltips: {
              mode: "index",
              intersect: false,
              callbacks: {
                label: function (tooltipItem: any) {
                  return ` ${Number(tooltipItem.yLabel).toLocaleString()} FCFA`;
                },
              },
            },
            scales: {
              xAxes: [
                {
                  gridLines: {
                    display: false,
                    color: "rgba(255, 255, 255, 0.1)",
                  },
                  ticks: { fontColor: "#94A3B8" },
                },
              ],
              yAxes: [
                {
                  gridLines: {
                    color: "rgba(255, 255, 255, 0.1)",
                    zeroLineColor: "rgba(255, 255, 255, 0.1)",
                  },
                  ticks: {
                    fontColor: "#94A3B8",
                    callback: function (value: any) {
                      return value.toLocaleString() + " FCFA";
                    },
                  },
                },
              ],
            },
          },
        });
      }
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [data, labels, loading]);

  return (
    <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-2xl bg-slate-800 border border-slate-700">
      <div className="rounded-t mb-0 px-4 py-3 bg-transparent">
        <div className="flex flex-wrap items-center">
          <div className="relative w-full max-w-full flex-grow flex-1">
            <h6 className="uppercase text-slate-400 mb-1 text-xs font-bold">
              {subtitle}
            </h6>
            <h2 className="text-white text-xl font-bold">{title}</h2>
          </div>
        </div>
      </div>
      <div className="p-4 flex-auto">
        <div className="relative h-[350px]">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <i className="fas fa-circle-notch animate-spin text-[#D4AF37] text-3xl"></i>
            </div>
          ) : data && data.length > 0 ? (
            <canvas ref={chartRef}></canvas>
          ) : (
            <div className="flex h-full items-center justify-center text-slate-500 italic">
              Données insuffisantes pour le graphique
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
